import { useTranslate } from '@va/localization';
import { useMemo } from 'react';
import { ProvisioningStep } from './ProvisioningSteps';

export const useSharedCustomDomainProvisioningSteps = () => {
  const translate = useTranslate();

  const checkingDNS = useMemo(
    () => ({ value: 'checking_dns', label: translate('customDomain.steps.checking_dns') }),
    [translate],
  );

  const creatingCDNConfig = useMemo(
    () => ({ value: 'creating_cdn_config', label: translate('customDomain.steps.creating_cdn_config') }),
    [translate],
  );

  const creatingSSLCertificate = useMemo(
    () => ({ value: 'creating_ssl_certificate', label: translate('customDomain.steps.creating_ssl_certificate') }),
    [translate],
  );

  const checkingSSLCertificateValidity = useMemo(
    () => ({
      value: 'checking_ssl_certificate_validity',
      label: translate('customDomain.steps.checking_ssl_certificate_validity'),
    }),
    [translate],
  );

  const finalCDNConfig = useMemo(
    () => ({ value: 'final_cdn_config', label: translate('customDomain.steps.final_cdn_config') }),
    [translate],
  );

  return { checkingDNS, creatingCDNConfig, creatingSSLCertificate, checkingSSLCertificateValidity, finalCDNConfig };
};

export const useAgencyCustomDashboardDomainProvisioningSteps = () => {
  const translate = useTranslate();

  const { checkingDNS, creatingCDNConfig, creatingSSLCertificate, checkingSSLCertificateValidity, finalCDNConfig } =
    useSharedCustomDomainProvisioningSteps();

  const steps = useMemo<ProvisioningStep[]>(() => {
    return [
      checkingDNS,
      creatingCDNConfig,
      creatingSSLCertificate,
      checkingSSLCertificateValidity,
      finalCDNConfig,
      {
        value: 'notifying_contributors_about_domain_change',
        label: translate('customDomain.steps.notifying_contributors_about_domain_change'),
      },
    ];
  }, [
    checkingDNS,
    checkingSSLCertificateValidity,
    creatingCDNConfig,
    creatingSSLCertificate,
    finalCDNConfig,
    translate,
  ]);

  return { steps };
};

export const useINTPCustomDomainProvisioningSteps = () => {
  const translate = useTranslate();

  const { checkingDNS, creatingCDNConfig, creatingSSLCertificate, checkingSSLCertificateValidity, finalCDNConfig } =
    useSharedCustomDomainProvisioningSteps();

  const steps = useMemo<ProvisioningStep[]>(() => {
    return [
      checkingDNS,
      creatingCDNConfig,
      creatingSSLCertificate,
      checkingSSLCertificateValidity,
      finalCDNConfig,
      {
        value: 'applying_domain_change',
        label: translate('customDomain.steps.applying_domain_change'),
      },
    ];
  }, [
    checkingDNS,
    checkingSSLCertificateValidity,
    creatingCDNConfig,
    creatingSSLCertificate,
    finalCDNConfig,
    translate,
  ]);

  return { steps };
};

export const useCustomSupportCenterDomainProvisioningSteps = () => {
  const { checkingDNS, creatingCDNConfig, creatingSSLCertificate, checkingSSLCertificateValidity, finalCDNConfig } =
    useSharedCustomDomainProvisioningSteps();

  const steps = useMemo<ProvisioningStep[]>(() => {
    return [checkingDNS, creatingCDNConfig, creatingSSLCertificate, checkingSSLCertificateValidity, finalCDNConfig];
  }, [checkingDNS, checkingSSLCertificateValidity, creatingCDNConfig, creatingSSLCertificate, finalCDNConfig]);

  return { steps };
};

export const useWebsiteTrackingCodeDomainProvisioningSteps = () => {
  const { checkingDNS, creatingCDNConfig, creatingSSLCertificate, checkingSSLCertificateValidity, finalCDNConfig } =
    useSharedCustomDomainProvisioningSteps();

  const steps = useMemo<ProvisioningStep[]>(() => {
    return [checkingDNS, creatingCDNConfig, creatingSSLCertificate, checkingSSLCertificateValidity, finalCDNConfig];
  }, [checkingDNS, checkingSSLCertificateValidity, creatingCDNConfig, creatingSSLCertificate, finalCDNConfig]);

  return { steps };
};
